import { useState, useEffect, useCallback } from 'react'
import { Loader2, RefreshCw, Play, XCircle } from 'lucide-react'
import { eventoSyncApi } from '@/services/api/eventoSync'
import type { SyncStatus, SyncLogEntry, SyncType } from '@/services/api/eventoSync'

interface EventoMonitorSectionProps {
  syncStatus: SyncStatus | null
  syncLogs: SyncLogEntry[]
  loading: boolean
  onRefresh: () => Promise<void>
  onSyncTriggered: () => void
  onSyncCompleted: () => void
}

export function EventoMonitorSection({ syncStatus, syncLogs, loading, onRefresh, onSyncTriggered, onSyncCompleted }: EventoMonitorSectionProps) {
  const [running, setRunning] = useState(false)
  const [syncType, setSyncType] = useState<SyncType>('full')
  const [syncing, setSyncing] = useState(false)
  const [expandedLogId, setExpandedLogId] = useState<string | null>(null)
  const [progress, setProgress] = useState<SyncStatus['current_sync'] | null>(null)

  useEffect(() => {
    if (syncStatus?.current_sync?.status === 'running') {
      setRunning(true)
      setProgress(syncStatus.current_sync)
    } else {
      setRunning(false)
      setProgress(null)
    }
  }, [syncStatus])

  useEffect(() => {
    if (!running) return

    let pollIntervalId: ReturnType<typeof setInterval> | null = null
    
    const poll = async () => {
      try {
        const status = await eventoSyncApi.getSyncStatus()
        if (status.current_sync) {
          setProgress(status.current_sync)
          if (status.current_sync.status === 'completed' || status.current_sync.status === 'failed') {
            if (pollIntervalId) clearInterval(pollIntervalId)
            setRunning(false)
            onSyncCompleted()
            onRefresh()
          }
        }
      } catch {
        // Ignore
      }
    }

    pollIntervalId = setInterval(poll, 2000)
    poll() // Initial poll

    return () => {
      if (pollIntervalId) clearInterval(pollIntervalId)
    }
  }, [running, onSyncCompleted, onRefresh])

  const handleSync = useCallback(async () => {
    setSyncing(true)
    onSyncTriggered()
    try {
      const result = await eventoSyncApi.triggerSync(syncType)
      if (result.success) {
        setRunning(true)
      }
    } catch {
      // Ignore
    } finally {
      setSyncing(false)
    }
  }, [syncType, onSyncTriggered])

  const handleCancel = useCallback(() => {
    setRunning(false)
    setProgress(null)
    onSyncCompleted()
  }, [onSyncCompleted])

  const statusColor = (s: string) => {
    switch (s) {
      case 'completed': return 'bg-green-100 text-green-700'
      case 'running': return 'bg-blue-100 text-blue-700'
      case 'failed': return 'bg-red-100 text-red-700'
      case 'partial': return 'bg-yellow-100 text-yellow-700'
      case 'disabled': return 'bg-gray-100 text-gray-500'
      default: return 'bg-gray-100 text-gray-700'
    }
  }

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-lg border p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold">Sync Status</h3>
          <button onClick={onRefresh} disabled={loading} className="px-3 py-1.5 border rounded hover:bg-gray-50 disabled:opacity-50 text-sm flex items-center gap-1">
            {loading ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
            Refresh
          </button>
        </div>

        {syncStatus ? (
          <div className="space-y-3">
            <div className={`text-sm font-medium ${syncStatus.enabled ? 'text-green-600' : 'text-red-600'}`}>
              {syncStatus.enabled ? 'Sync ist aktiviert' : 'Sync ist deaktiviert'}
            </div>
            {syncStatus.api_url_configured ? (
              <p className="text-sm text-gray-600">API URL: konfiguriert</p>
            ) : (
              <p className="text-sm text-gray-400">API URL: nicht konfiguriert</p>
            )}
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div>Frequenz: <span className="font-medium">{syncStatus.sync_frequency}</span></div>
              <div>Strategie: <span className="font-medium">{syncStatus.strategy}</span></div>
              <div>Letzter Sync: <span className="font-medium">{syncStatus.last_sync_at ? new Date(syncStatus.last_sync_at).toLocaleString('de-CH') : 'Nie'}</span></div>
            </div>

            {progress && (
              <div className="mt-4 p-4 bg-blue-50 rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-blue-700">Sync läuft...</span>
                  <button onClick={handleCancel} className="text-xs text-red-600 hover:text-red-700 flex items-center gap-1">
                    <XCircle className="w-3 h-3" /> Abbrechen
                  </button>
                </div>
                <div className="flex justify-between text-xs mb-1 text-gray-600">
                  <span>Staff: {progress.staff_completed}/{progress.staff_total}</span>
                  <span>Timetable: {progress.timetable_completed}/{progress.timetable_total}</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div className="bg-blue-600 h-2 rounded-full transition-all" style={{ width: `${progress.percent}%` }} />
                </div>
                <div className="text-xs text-gray-500 mt-1">{progress.percent}%</div>
              </div>
            )}

            <div className="pt-2 border-t">
              <h4 className="text-sm font-medium text-gray-700 mb-1">Aktivierte Typen</h4>
              <div className="flex gap-2">
                {Object.entries(syncStatus.sync_types).map(([key, enabled]) => (
                  <span key={key} className={`px-2 py-0.5 text-xs rounded-full ${enabled ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-400'}`}>
                    {key} {enabled ? 'ON' : 'OFF'}
                  </span>
                ))}
              </div>
            </div>

            <div className="pt-2 border-t">
              <h4 className="text-sm font-medium text-gray-700 mb-2">Manueller Sync</h4>
              <div className="flex gap-3 items-center">
                <select value={syncType} onChange={e => setSyncType(e.target.value as SyncType)} className="px-3 py-2 border rounded-lg text-sm">
                  <option value="full">Full Sync</option>
                  <option value="staff_only">Nur Lehrer</option>
                  <option value="timetable_only">Nur Stundenplan</option>
                  <option value="calendar_only">Nur Ferien</option>
                </select>
                <button onClick={handleSync} disabled={syncing || running} className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 text-sm flex items-center gap-2">
                  {syncing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />}
                  Sync starten
                </button>
              </div>
            </div>
          </div>
        ) : (
          <p className="text-sm text-gray-400">Laden...</p>
        )}
      </div>

      <div className="bg-white rounded-lg border p-6">
        <h3 className="font-semibold mb-4">Sync Verlauf</h3>
        {loading ? (
          <p className="text-sm text-gray-400">Laden...</p>
        ) : syncLogs.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Datum</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Typ</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Ausgelöst</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Dauer</th>
                  <th className="px-3 py-2"></th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {syncLogs.map(log => (
                  <>
                    <tr key={log.id}>
                      <td className="px-3 py-2 text-xs text-gray-500">{new Date(log.started_at).toLocaleString('de-CH')}</td>
                      <td className="px-3 py-2 text-xs font-medium">{log.sync_type}</td>
                      <td className="px-3 py-2 text-xs text-gray-500">{log.triggered_by}</td>
                      <td className="px-3 py-2">
                        <span className={`px-2 py-0.5 text-xs rounded-full ${statusColor(log.status)}`}>
                          {log.status}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-xs text-gray-500">
                        {log.duration_seconds ? `${log.duration_seconds}s` : '—'}
                      </td>
                      <td className="px-3 py-2">
                        <button onClick={() => setExpandedLogId(expandedLogId === log.id ? null : log.id)} className="text-xs text-blue-600 hover:text-blue-700">
                          {expandedLogId === log.id ? 'Schliessen' : 'Details'}
                        </button>
                      </td>
                    </tr>
                    {expandedLogId === log.id && (
                      <tr>
                        <td colSpan={6} className="px-6 py-4 bg-gray-50">
                          <div className="space-y-2 text-xs">
                            {log.staff_results && (
                              <div className="flex gap-4">
                                <span className="font-medium">Staff:</span>
                                <span>Created: {log.staff_results.created || 0}</span>
                                <span>Updated: {log.staff_results.updated || 0}</span>
                              </div>
                            )}
                            {log.timetable_results && (
                              <div className="flex gap-4">
                                <span className="font-medium">Timetable:</span>
                                <span>Created: {log.timetable_results.created || 0}</span>
                                <span>Updated: {log.timetable_results.updated || 0}</span>
                              </div>
                            )}
                            {log.calendar_results && (
                              <div className="flex gap-4">
                                <span className="font-medium">Calendar:</span>
                                <span>Created: {log.calendar_results.created || 0}</span>
                              </div>
                            )}
                            {log.errors && log.errors.length > 0 && (
                              <div>
                                <span className="font-medium text-red-600">Errors:</span>
                                <ul className="list-disc list-inside mt-1 text-red-600">
                                  {log.errors.map((e, i) => <li key={i}>{e}</li>)}
                                </ul>
                              </div>
                            )}
                            {log.warnings && log.warnings.length > 0 && (
                              <div>
                                <span className="font-medium text-yellow-600">Warnings:</span>
                                <ul className="list-disc list-inside mt-1 text-yellow-600">
                                  {log.warnings.map((w, i) => <li key={i}>{w}</li>)}
                                </ul>
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="text-sm text-gray-400 text-center py-4">Keine Sync-Historie vorhanden</p>
        )}
      </div>
    </div>
  )
}